import { type ReactNode } from 'react'

import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import {
  ContractDuration,
  ContractDurationTimeRange,
  ContractOrderFrequency,
  RetailPerOrder,
} from '@b-stock/fe-core/bstock-next/components'
import {
  formatAddressShort,
  getNumberOfUnitsInLot,
  isLotUnmanifested,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedListingDetails,
  type OrchestratedLotWithGroups,
  inventoryGroupsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  CurrencyStringCents,
  FormattedMessage,
  InteractiveIcon,
  Link,
  LotRollup,
  LotRollupHelpers,
} from '@b-stock/fe-core/bstock-react'
import { CommonStrings } from '@b-stock/fe-core/locale'

import { AccordionCard } from '@/buyer-portal/components/layouts/CardLayout/card/AccordionCard'
import {
  CardGrid,
  CellSmallValue,
  CellSubValue,
  CellValue,
  SuccessCellValue,
  type CardGridItem,
} from '@/buyer-portal/components/layouts/CardLayout/card/CardGrid'
import { useAllConditionsDisplay } from '@/buyer-portal/helpers/lot/conditions'
import { FeatureFlags } from '@/helpers/featureFlags'

import { calculateShipmentDetails } from '../Shipment/utils'

const CONDITIONS_FAQ_URL =
  'https://bstock.com/faq/buyer-faq/bidding/what-are-the-bstock-product-and-packing-conditions/'

// ContractDuration / ContractDurationTimeRange each render a <div>; keep them
// inline so the "date range | duration" value stays on one line in the cell.
const ContractDurationValue = styled(CellValue)`
  & > div {
    display: inline;
  }
`

const isRollupVisible = (
  lot: OrchestratedLotWithGroups,
  data: unknown[] | undefined,
  attributeKey: string
) =>
  isLotUnmanifested(lot)
    ? (data?.length ?? 0) > 0
    : (lot.attributeOrder ?? []).includes(attributeKey)

const rollupCell = (
  id: string,
  labelId: string,
  rollup: ReactNode
): CardGridItem => ({
  id,
  testId: `listing-details-${id}`,
  label: <FormattedMessage id={labelId} />,
  value: <CellSmallValue>{rollup}</CellSmallValue>,
})

export const ListingDetailsCard = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const inventoryGroupsResult = inventoryGroupsQuery.useQuery(listing.lot._id)
  const groups = inventoryGroupsResult.data?.groups ?? []
  const isGroupsLoading = inventoryGroupsResult.isLoading

  const { totalPalletSpaces, showPalletSpaces } =
    calculateShipmentDetails(groups)

  const contract = listing.contract
  const lot = listing.lot
  const transportMode = listing.shipping?.transportMode
  const truckCount = listing.shipping?.truckCount

  const shortAddress = formatAddressShort(lot.location, true)

  const isManifested = !isLotUnmanifested(lot)

  const condition = useAllConditionsDisplay(lot)

  const isContract = listing.listingType === 'CONTRACT'
  const msrpOrCogs = listing.contract?.bidUnit === 'COGS' ? 'COGS' : 'MSRP'

  const manifestUnits = getNumberOfUnitsInLot(lot) ?? 0
  const manifestTotalMSRP = lot.retailValue

  const enableMemoryRollupVisibility = useFlag(
    FeatureFlags.ENABLE_MEMORY_ROLLUP_VISIBILITY
  )
  const sellerConditionGradeFlag = useFlag(
    FeatureFlags.ENABLE_LDP_SELLER_CONDITION_GRADE_ENHANCEMENT
  )

  // LotDto doesn't yet have `memories` — backend support is pending.
  // When backend ships, remove this assertion and use the real typed field.
  const lotWithMemories = lot as OrchestratedLotWithGroups &
    LotRollupHelpers.LotWithMemories

  const modelsVisible = isRollupVisible(
    lot,
    lot.models,
    'item.attributes.model'
  )
  const manufacturersVisible = isRollupVisible(
    lot,
    lot.manufacturers,
    'item.attributes.item.manufacturer'
  )
  const capacitiesVisible = isRollupVisible(
    lot,
    lot.capacities,
    'item.attributes.item.capacity'
  )
  const carrierVisible = isRollupVisible(
    lot,
    lot.carriers,
    'item.attributes.item.carrier'
  )
  const memoriesVisible =
    enableMemoryRollupVisibility &&
    isRollupVisible(
      lotWithMemories,
      lotWithMemories.memories,
      'item.attributes.memory'
    )
  const sellerGradeVisible = isRollupVisible(
    lot,
    lot.mobileDevice?.sellerGrades,
    'item.mobileDevice.sellerGrade'
  )

  // ENABLE_LDP_SELLER_CONDITION_GRADE_ENHANCEMENT: for whitelisted storefronts
  // (AT&T) suppress "Condition" when "Seller Grade" is present.
  const showCondition = (() => {
    const id = listing.seller.storefront?._id
    const slug = listing.seller.storefront?.slug
    const flag = sellerConditionGradeFlag
    if (
      flag &&
      flag.enabled &&
      ((id && flag.includedStorefrontIds?.includes(id)) ||
        (slug && flag.includedStorefrontSlugs?.includes(slug)))
    ) {
      return !sellerGradeVisible
    }
    return true
  })()

  const manifestSummaryValue = (() => {
    const hasUnits = manifestUnits > 0
    const hasMSRP = !!manifestTotalMSRP && manifestTotalMSRP > 0

    if (hasUnits && hasMSRP) {
      return (
        <FormattedMessage
          id="Listing.details.manifestSummaryUnitsMsrp"
          values={{
            units: manifestUnits,
            msrp: (
              <CurrencyStringCents
                value={manifestTotalMSRP}
                currency={listing.currency || 'USD'}
              />
            ),
          }}
        />
      )
    }
    if (hasMSRP) {
      return (
        <FormattedMessage
          id="Listing.details.manifestSummaryMsrp"
          values={{
            msrp: (
              <CurrencyStringCents
                value={manifestTotalMSRP}
                currency={listing.currency || 'USD'}
              />
            ),
          }}
        />
      )
    }
    if (hasUnits) {
      return (
        <FormattedMessage
          id="Listing.details.manifestSummaryUnits"
          values={{ units: manifestUnits }}
        />
      )
    }
    return null
  })()

  const manifestSummaryItems: CardGridItem[] =
    !isContract && manifestSummaryValue
      ? [
          {
            id: 'manifestSummary',
            testId: 'listing-details-manifestSummary',
            label: <FormattedMessage id="Listing.details.manifestSummary" />,
            value: <CellValue>{manifestSummaryValue}</CellValue>,
          },
        ]
      : []

  const NA = <CommonStrings.NotAvailableAbbr.Component />
  const contractTimeRangeNode =
    contract?.startsAt && contract.endsAt ? (
      <ContractDurationTimeRange contract={contract} variant="verbose" />
    ) : null
  const contractDurationNode = contract?.duration ? (
    <ContractDuration contract={contract} />
  ) : null

  const contractItems: CardGridItem[] =
    isContract && listing.contract
      ? [
          {
            id: 'contractDuration',
            testId: 'listing-details-contractDuration',
            label: <FormattedMessage id="Listing.details.contractDuration" />,
            value: (
              <ContractDurationValue>
                {contractTimeRangeNode && contractDurationNode ? (
                  <>
                    {contractTimeRangeNode}
                    {' | '}
                    {contractDurationNode}
                  </>
                ) : (
                  (contractTimeRangeNode ?? contractDurationNode ?? NA)
                )}
              </ContractDurationValue>
            ),
          },
          {
            id: 'estRetailPerOrder',
            testId: 'listing-details-estRetailPerOrder',
            label: (
              <FormattedMessage
                id="Listing.details.estRetailPerOrder"
                values={{ msrpOrCogs }}
              />
            ),
            value: (
              <CellValue>
                <RetailPerOrder
                  contract={listing.contract}
                  currency={listing.currency || 'USD'}
                />
              </CellValue>
            ),
          },
          {
            id: 'estOrderFrequency',
            testId: 'listing-details-estOrderFrequency',
            label: <FormattedMessage id="Listing.details.estOrderFrequency" />,
            value: (
              <CellValue>
                <ContractOrderFrequency contract={listing.contract} />
              </CellValue>
            ),
          },
        ]
      : []

  // Unmanifested lots have no condition data yet (it arrives with the
  // manifest), so hide the cells rather than claiming it's "Unspecified".
  // A manifested lot with no condition data is a real Unspecified value —
  // show it consistently with how the rest of the platform (contract
  // details, dispute cards) renders a missing condition.
  const conditionItems: CardGridItem[] =
    showCondition && (!!condition || isManifested)
      ? [
          {
            id: 'condition',
            testId: 'listing-details-condition',
            label: (
              <>
                <FormattedMessage id="Listing.details.condition" />
                <InteractiveIcon tooltipProps={{ hoverable: true }}>
                  <FormattedMessage
                    id="Listing.details.conditionTooltip"
                    values={{
                      link: (chunks: ReactNode) => (
                        <Link
                          href={CONDITIONS_FAQ_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {chunks}
                        </Link>
                      ),
                    }}
                  />
                </InteractiveIcon>
              </>
            ),
            value: (
              <CellValue>
                {condition ?? <CommonStrings.Unspecified.Component />}
              </CellValue>
            ),
          },
          rollupCell(
            'conditionBreakdown',
            'Listing.details.conditionBreakdown',
            <LotRollup
              {...LotRollupHelpers.itemConditions(lot)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []

  const items: CardGridItem[] = [
    {
      id: 'mode',
      label: <FormattedMessage id="Listing.details.mode" />,
      value: (
        <>
          <CellValue>{transportMode ?? '—'}</CellValue>
          {transportMode && (
            <CellSubValue>
              <FormattedMessage
                id={`Auction.shipment.details.transportModes.${transportMode}`}
              />
            </CellSubValue>
          )}
        </>
      ),
    },
    {
      id: 'palletSpaces',
      testId: 'listing-details-palletSpaces',
      label: <FormattedMessage id="Listing.details.palletSpaces" />,
      value: (
        <>
          <CellValue>
            {isGroupsLoading || !showPalletSpaces ? (
              '—'
            ) : (
              <FormattedNumber value={totalPalletSpaces} />
            )}
          </CellValue>
          {showPalletSpaces && !!truckCount && (
            <CellSubValue>
              <FormattedMessage
                id="Listing.details.truckloadsCount"
                values={{ count: truckCount }}
              />
            </CellSubValue>
          )}
        </>
      ),
    },
    {
      id: 'manifest',
      label: <FormattedMessage id="Listing.details.manifest" />,
      value: isManifested ? (
        <SuccessCellValue>
          <FormattedMessage id="Listing.details.manifestYes" />
        </SuccessCellValue>
      ) : (
        <CellValue>
          <FormattedMessage id="Listing.details.manifestNo" />
        </CellValue>
      ),
    },
    ...manifestSummaryItems,
    ...contractItems,
    rollupCell(
      'inventoryType',
      'Listing.details.inventoryType',
      <LotRollup
        {...LotRollupHelpers.inventoryTypes(lot)}
        numShown={4}
        showPercentages
      />
    ),
    rollupCell(
      'categoryBreakdown',
      'Listing.details.categoryBreakdown',
      <LotRollup
        {...LotRollupHelpers.categories(lot)}
        numShown={4}
        showPercentages
      />
    ),
    {
      id: 'origin',
      label: <FormattedMessage id="Listing.details.origin" />,
      value: <CellValue>{shortAddress || '—'}</CellValue>,
    },
    ...conditionItems,
    ...(sellerGradeVisible
      ? [
          rollupCell(
            'sellerGrade',
            'Listing.details.sellerGrade',
            <LotRollup
              {...LotRollupHelpers.sellerGrades(lot)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []),
    ...(modelsVisible
      ? [
          rollupCell(
            'models',
            'Listing.details.models',
            <LotRollup
              {...LotRollupHelpers.models(lot)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []),
    ...(manufacturersVisible
      ? [
          rollupCell(
            'manufacturers',
            'Listing.details.manufacturers',
            <LotRollup
              {...LotRollupHelpers.manufacturers(lot)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []),
    ...(capacitiesVisible
      ? [
          rollupCell(
            'capacities',
            'Listing.details.capacities',
            <LotRollup
              {...LotRollupHelpers.capacities(lot)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []),
    ...(carrierVisible
      ? [
          rollupCell(
            'carrier',
            'Listing.details.carrier',
            <LotRollup
              {...LotRollupHelpers.carriers(lot)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []),
    ...(memoriesVisible
      ? [
          rollupCell(
            'memories',
            'Listing.details.memories',
            <LotRollup
              {...LotRollupHelpers.memories(lotWithMemories)}
              numShown={4}
              showPercentages
            />
          ),
        ]
      : []),
  ]

  return (
    <AccordionCard title={<FormattedMessage id="Listing.details.title" />}>
      <CardGrid items={items} />
    </AccordionCard>
  )
}
